import { useTranslation } from '../i18n';
import { useNbTheme } from '../hooks/useNbTheme';
import IconNB from './ui-nb/IconNB';

interface Stage {
  key: string;
  icon: string;
  step: number;
}

const stages: Stage[] = [
  { key: 'checkin', icon: 'orders', step: 1 },
  { key: 'diagnose', icon: 'search', step: 2 },
  { key: 'repair', icon: 'settings', step: 3 },
  { key: 'qualityCheck', icon: 'check', step: 4 },
  { key: 'notify', icon: 'bell', step: 5 },
  { key: 'pickup', icon: 'equipment', step: 6 },
];

export default function RepairJourneyNB() {
  const { t } = useTranslation();
  const { colors } = useNbTheme();

  return (
    <section
      className="py-12 md:py-20"
      style={{ background: colors.bgSection || '#F0F0F0' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="mb-8 md:mb-12">
          <h2
            className="font-nb font-bold mb-3"
            style={{
              color: colors.textPrimary,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.2,
            }}
          >
            {t('journey.title')}
          </h2>
          <p
            className="font-nb max-w-xl"
            style={{
              color: colors.textSecondary,
              fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
              lineHeight: 1.6,
            }}
          >
            {t('journey.subtitle')}
          </p>
        </div>

        {/* Horizontal scroll container */}
        <div className="relative">
          {/* Timeline connector line — behind the cards (desktop only) */}
          <div
            className="absolute top-[48px] left-0 right-0 hidden md:block"
            style={{ height: '3px', background: colors.textPrimary, opacity: 0.15 }}
          />

          {/* Scrollable row: flex, items-stretch to fill height of tallest card */}
          <div
            className="flex flex-row items-stretch gap-4 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-nb"
          >
            {stages.map((stage) => (
              <div
                key={stage.key}
                className="snap-center flex-shrink-0 w-[85vw] sm:w-[60vw] md:w-[300px] lg:w-[340px]"
              >
                <div
                  className="border-3 p-4 md:p-6 h-full relative transition-colors duration-200"
                  style={{
                    background: colors.bgCard,
                    borderColor: colors.textPrimary,
                    borderRadius: '2px',
                  }}
                >
                  {/* Step number circle — "O" circle, vertically centered in card on mobile */}
                  <div
                    className="w-10 h-10 rounded-full border-3 flex items-center justify-center mb-4 font-nb font-bold text-sm mx-auto relative z-10"
                    style={{
                      borderColor: colors.textPrimary,
                      background:
                        stage.step === 1
                          ? colors.accentPrimary || '#FFD600'
                          : colors.bgCard,
                      color: stage.step === 1 ? '#1C1C1C' : colors.textPrimary,
                    }}
                  >
                    {stage.step}
                  </div>

                  {/* Icon */}
                  <div className="w-8 h-8 mx-auto mb-3 flex items-center justify-center">
                    <IconNB
                      name={stage.icon}
                      size={28}
                      style={{ color: colors.textPrimary }}
                    />
                  </div>

                  {/* Title */}
                  <h3
                    className="font-nb font-bold text-lg mb-2 text-center"
                    style={{ color: colors.textPrimary }}
                  >
                    {t(`journey.stages.${stage.key}.title`)}
                  </h3>

                  {/* Description */}
                  <p
                    className="font-nb text-sm text-center leading-relaxed"
                    style={{ color: colors.textSecondary }}
                  >
                    {t(`journey.stages.${stage.key}.description`)}
                  </p>
                </div>
              </div>
            ))}

            {/* Spacer — ensures last card doesn't hug edge */}
            <div className="flex-shrink-0 w-4 md:w-8" />
          </div>
        </div>
      </div>
    </section>
  );
}
